// =============================================================================
// Normascan — Plateforme d'analyse normative par IA
// Copyright (c) 2026-2027 Evalens SAS — Tous droits réservés
// Marque Normascan déposée INPI n°5242986 (classes 9 et 42)
// Site : https://app.normascan.fr
// =============================================================================
//
// Bloc « Consommations » tel que le voit un CLIENT (Mes statistiques, portail
// licence) — par opposition à celui de la page Statistiques admin, qui reste
// inchangé et continue d'exposer tokens, ratio token/page et typologie d'appel.
//
// Le vocabulaire est métier : un diagnostiqueur achète des analyses, pas des
// appels LLM. `tokens_in`, `token_page_ratio` et `call_type` ne veulent rien
// dire pour lui et ne sont d'ailleurs plus renvoyés par l'API côté client.
//
// Partagé par MesStatistiques.js (portée personnelle) et LicensePortal.js
// (portée équipe) : c'est la même vue, seule la portée change côté serveur.
import React from 'react';
import { useI18n } from '../i18n';
import BarChartDouble from './BarChartDouble';

const selectStyle = { padding: '5px 8px', borderRadius: 6, border: '1px solid var(--gray)', background: 'var(--bg-input)', color: 'var(--black)', fontSize: 12 };

const Chiffre = ({ label, valeur, couleur }) => (
  <div>
    <p style={{ fontSize: 11, color: 'var(--text-muted)', margin: 0 }}>{label}</p>
    <p style={{ fontSize: 20, fontWeight: 700, color: couleur || 'var(--black)', margin: 0 }}>{valeur}</p>
  </div>
);

const ConsommationsClient = ({ data, granularite, annee, mois, onChange, actions = null }) => {
  const { t, lang } = useI18n();
  const locale = lang === 'en' ? 'en-GB' : 'fr-FR';

  // Les noms de mois viennent d'`Intl`, pas du catalogue : c'est de la donnée
  // formatée selon la langue active, au même titre que les dates affichées
  // ailleurs sur cette page — pas une chaîne d'interface à traduire à la main.
  const nomMois = (numero, style = 'long') =>
    new Date(2000, numero - 1, 1).toLocaleDateString(locale, { month: style });

  const vueAnnuelle = granularite === 'annee';
  const annees = data?.annees_disponibles?.length ? data.annees_disponibles : [annee];

  const points = (data?.serie || []).map(p => ({
    cle: p.cle, gauche: p.analyses, droite: p.pages_ocr,
  }));

  const labelX = (cle) => (vueAnnuelle ? nomMois(cle, 'short') : String(cle));
  const titrePoint = (p) => {
    const tete = vueAnnuelle ? nomMois(p.cle) : `${t('stat_axe_jour')} ${p.cle}`;
    return `${tete} — ${p.gauche} ${t('stat_serie_analyses')} · ${p.droite} ${t('stat_serie_pages_ocr')}`;
  };

  const periodeVide = !!data && !data.total_analyses && !data.total_pages_ocr;

  return (
    <div style={{ background: 'var(--bg-surface)', borderRadius: 12, border: '1px solid var(--gray)', padding: '20px 24px', marginTop: 20 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
        <h2 style={{ fontSize: 15, fontWeight: 700, color: 'var(--black)', margin: 0 }}>{t('stat_consommations_title')}</h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <select value={granularite} onChange={e => onChange({ granularite: e.target.value })} style={selectStyle}>
            <option value="mois">{t('stat_periode_mensuelle')}</option>
            <option value="annee">{t('stat_periode_annuelle')}</option>
          </select>
          {!vueAnnuelle && (
            <select value={mois} onChange={e => onChange({ mois: Number(e.target.value) })} style={selectStyle}>
              {Array.from({ length: 12 }, (_, i) => i + 1).map(m => (
                <option key={m} value={m}>{nomMois(m)}</option>
              ))}
            </select>
          )}
          <select value={annee} onChange={e => onChange({ annee: Number(e.target.value) })} style={selectStyle}>
            {annees.map(a => <option key={a} value={a}>{a}</option>)}
          </select>
          {actions}
        </div>
      </div>

      <div style={{ display: 'flex', gap: 24, marginBottom: 18, flexWrap: 'wrap' }}>
        <Chiffre label={t('stat_total_analyses_periode')} valeur={data?.total_analyses ?? '—'} couleur="var(--blue)" />
        <Chiffre label={t('stat_total_pages_ocr_periode')} valeur={data?.total_pages_ocr ?? '—'} couleur="var(--gold)" />
        <Chiffre label={t('stat_avg_pages_analysees')} valeur={data?.avg_pages ?? '—'} />
      </div>

      <BarChartDouble
        points={points}
        labelX={labelX}
        labelSerieGauche={t('stat_serie_analyses')}
        labelSerieDroite={t('stat_serie_pages_ocr')}
        titrePoint={titrePoint}
        ariaLabel={t('stat_graph_aria')}
      />

      {periodeVide && (
        <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 12, marginBottom: 0 }}>
          {t('stat_consommations_empty')}
        </p>
      )}
    </div>
  );
};

export default ConsommationsClient;
